import React, { Suspense, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AppBar } from "../../components";
import { GlobalSpinner } from "../../components/Spinner";
import Chat from "./Chat";
import "./index.scss";
import { collection, query, onSnapshot } from "@firebase/firestore";
import { db, messaging } from "../../firebase";
import { getToken } from "firebase/messaging";
import api from "../../utils/axios";
import { hasModulePermission } from "../../utils/permissionHelper";
import { actionGetUserDataWithToken } from "../../actions/auth";


const Sidebar = React.lazy(() => import("../../components/Sidebar"));
const DashBoardScreen = React.lazy(() => import("./DashboardScreen"));
const Products = React.lazy(() => import("./Products"));
const ProductDetails = React.lazy(() => import("./Products/Details"));
const OrderDetails = React.lazy(() => import("./Orders/Details"));
const Orders = React.lazy(() => import("./Orders"));
const Customers = React.lazy(() => import("./UserManagement/Customers"));
const CustomerDetails = React.lazy(
  () => import("./UserManagement/Customers/Details"),
);
const SalesUsers = React.lazy(() => import("./UserManagement/SalesUsers"));
const SalesUserDetails = React.lazy(
  () => import("./UserManagement/SalesUsers/Details"),
);
const SubAdmins = React.lazy(() => import("./UserManagement/SubAdmins"));
const SubAdminDetails = React.lazy(
  () => import("./UserManagement/SubAdmins/Details"),
);
const UserGroups = React.lazy(() => import("./UserManagement/UserGroups"));
const UserGroupDetails = React.lazy(
  () => import("./UserManagement/UserGroups/Details"),
);
const PromotionManagement = React.lazy(() => import("./PromotionManagement"));
const OfferManagement = React.lazy(() => import("./OfferManagement"));
const OfferDetails = React.lazy(
  () => import("./UserManagement/Customers/Details/Tabs/Offers/OfferDetails"),
);
const OrderConfirm = React.lazy(() => import("./Orders/ConfirmPage"));
const MembershipManagement = React.lazy(() => import("./MembershipManagement"));
const Memberships = React.lazy(() => import("./Memberships"));
const MyProfile = React.lazy(() => import("./Profile"));
const Story = React.lazy(() => import("./Story"));
const DashboardBannerManagement = React.lazy(
  () => import("./DashboardBannerManagement"),
);
const AiNotifications = React.lazy(() => import("./AiNotifications"));
const CategoryFields = React.lazy(() => import("./CategoryFields"));

export const DashBoard = React.memo((props) => {
  const { isAuthenticated } = useSelector((state) => state?.authReducer);
  const navigate = useNavigate();
  const location = useLocation();
  const lastMessageTimesRef = useRef({});

  useEffect(() => {
    if (!isAuthenticated) return;

    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const registerFCM = async () => {
      try {
        if ("serviceWorker" in navigator && "Notification" in window) {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
            if (!vapidKey) {
              console.warn(
                "🔔 FCM VAPID Key is missing in .env! Background push notifications require REACT_APP_FIREBASE_VAPID_KEY to be set.",
              );
              return;
            }
            const registration = await navigator.serviceWorker.register(
              "/firebase-messaging-sw.js",
            );
            const token = await getToken(messaging, {
              vapidKey,
              serviceWorkerRegistration: registration,
            });
            if (token) {
              await api.post("/user/update-device-token", {
                device_token: token,
              });
            }
          }
        }
      } catch (err) {
        console.error("🔔 Error registering FCM device token:", err);
      }
    };
    registerFCM();

    const q = query(collection(db, "users"));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      // If we are currently on the chats page, let the Chat component handle notifications
      if (location.pathname === "/chats") return;

      querySnapshot.docChanges().forEach((change) => {
        if (change.type === "modified" || change.type === "added") {
          const docData = change.doc.data();
          const userEmail = docData.email;
          if (!userEmail) return;

          const getTimestampSeconds = (t) => {
            if (!t) return 0;
            if (typeof t.seconds === "number") return t.seconds;
            if (typeof t.toMillis === "function") return t.toMillis() / 1000;
            if (t instanceof Date) return t.getTime() / 1000;
            if (typeof t === "number") return t / 1000;
            return 0;
          };

          const lastTime = docData.lastMessageTime
            ? getTimestampSeconds(docData.lastMessageTime)
            : 0;
          const prevTime = lastMessageTimesRef.current[userEmail] || 0;

          // Update the cache
          lastMessageTimesRef.current[userEmail] = lastTime;

          // Only notify if it's a new message, cache was already initialized, and it's unseen
          if (lastTime > prevTime && prevTime > 0 && docData.isSeen === false) {
            // Play sound
            try {
              const audioCtx = new (
                window.AudioContext || window.webkitAudioContext
              )();
              const playChime = (time, frequency) => {
                const osc = audioCtx.createOscillator();
                const gainNode = audioCtx.createGain();
                osc.type = "sine";
                osc.frequency.setValueAtTime(frequency, time);
                gainNode.gain.setValueAtTime(0.35, time);
                gainNode.gain.exponentialRampToValueAtTime(0.01, time + 0.35);
                osc.connect(gainNode);
                gainNode.connect(audioCtx.destination);
                osc.start(time);
                osc.stop(time + 0.35);
              };
              const now = audioCtx.currentTime;
              playChime(now, 587.33); // D5 chime
              playChime(now + 0.12, 880); // A5 chime
            } catch (error) {
              console.error("Failed to play notification sound:", error);
            }

            // Show desktop notification
            if (
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              const notification = new Notification(
                `New message from ${docData.name || userEmail}`,
                {
                  body: docData.lastMessage,
                  icon: "/favicon.ico",
                },
              );
              notification.onclick = () => {
                window.focus();
                navigate(`/chats?email=${encodeURIComponent(userEmail)}`);
              };
            }
          }
        }
      });
    });

    return () => unsubscribe();
  }, [isAuthenticated, location.pathname, navigate]);

  const reduxUser = useSelector((state) => state?.authReducer?.user);
  const user = reduxUser && Object.keys(reduxUser).length > 0 ? reduxUser : JSON.parse(localStorage.getItem("userData") || "{}");
  const dispatch = useDispatch();
  const [masterPermissions, setMasterPermissions] = React.useState([]);

  React.useEffect(() => {
    const userTypeStr = String(user?.usertype || user?.user_type || "").toLowerCase().trim();
    const isSubAdmin =
      userTypeStr === "subadmin" ||
      userTypeStr === "sub_admin" ||
      userTypeStr === "sub-admin" ||
      Boolean(user?.sub_admin_permissions) ||
      Boolean(user?.permission);
    if (isSubAdmin && masterPermissions.length === 0) {
      const fetchPermissions = async () => {
        try {
          const { actionGetPermissions } = await import("../../actions/Dashboard/UserManagement");
          const res = await dispatch(actionGetPermissions());
          if (res?.success && Array.isArray(res?.data)) {
            setMasterPermissions(res.data);
          }
        } catch (err) {
          console.error("Failed to fetch master permissions", err);
        }
      };
      fetchPermissions();
    }
  }, [user, masterPermissions.length, dispatch]);

  React.useEffect(() => {
    if (isAuthenticated) {
      dispatch(actionGetUserDataWithToken());
    }
  }, [location.pathname, isAuthenticated, dispatch]);



  const PrivateRoute = ({ children, permissionKey }) => {
    if (!isAuthenticated) {
      return <Navigate to="/auth/login" />;
    }
    if (permissionKey && !hasModulePermission(user, permissionKey, masterPermissions)) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  return (
    <Suspense fallback={<GlobalSpinner />}>
      <div className="dashboard">
        <Sidebar />
        <div className="main">
          <AppBar />
          <Routes>
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashBoardScreen />
                </PrivateRoute>
              }
            />
            <Route
              path="/products"
              element={
                <PrivateRoute permissionKey="Products">
                  <Products />
                </PrivateRoute>
              }
            />
            <Route
              path="/products/:id/details"
              element={
                <PrivateRoute permissionKey="Products">
                  <ProductDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute permissionKey="Orders">
                  <Orders />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/:id/details"
              element={
                <PrivateRoute permissionKey="Orders">
                  <OrderDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <PrivateRoute permissionKey="Customers">
                  <Customers />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/customer/:id/details"
              element={
                <PrivateRoute permissionKey="Customers">
                  <CustomerDetails />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/customer/offer/:id/details"
              element={
                <PrivateRoute permissionKey="Customers">
                  <OfferDetails />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-users"
              element={
                <PrivateRoute permissionKey="Sales users">
                  {" "}
                  <SalesUsers />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-users/:id/details"
              element={
                <PrivateRoute permissionKey="Sales users">
                  {" "}
                  <SalesUserDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/sub-admins"
              element={
                <PrivateRoute permissionKey="Sub admins">
                  <SubAdmins />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/sub-admins/:id/details"
              element={
                <PrivateRoute permissionKey="Sub admins">
                  <SubAdminDetails />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/user-groups"
              element={
                <PrivateRoute permissionKey="User groups">
                  <UserGroups />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/user-groups/:id/details"
              element={
                <PrivateRoute permissionKey="User groups">
                  <UserGroupDetails />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/promotion-management"
              element={
                <PrivateRoute permissionKey="Promotion Management">
                  <PromotionManagement />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/offer-management"
              element={
                <PrivateRoute permissionKey="Promotion Management">
                  <OfferManagement />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/confirm-order"
              element={
                <PrivateRoute permissionKey="Orders">
                  <OrderConfirm />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/membership-management"
              element={
                <PrivateRoute permissionKey="Membership Management">
                  <MembershipManagement />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/memberships"
              element={
                <PrivateRoute permissionKey="Memberships">
                  <Memberships />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/story"
              element={
                <PrivateRoute permissionKey="Story">
                  <Story />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard-banners"
              element={
                <PrivateRoute permissionKey="Dashboard Banners">
                  <DashboardBannerManagement />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-notifications"
              element={
                <PrivateRoute permissionKey="AI Notifications">
                  <AiNotifications />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/category-fields"
              element={
                <PrivateRoute permissionKey="Category Fields">
                  <CategoryFields />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/chats"
              element={
                <PrivateRoute permissionKey="Chats">
                  <Chat />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <PrivateRoute>
                  <MyProfile />{" "}
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to={"/404"} />} />
          </Routes>
        </div>
      </div>
    </Suspense>
  );
});
